import { motion } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import clsx from "clsx";
import wazeIcon from "../assets/waze.svg";
import logo from "../assets/logo.webp";

const navItems = [
  { name: "אודות", href: "#about" },
  { name: "תחומי עיסוק", href: "#services" },
  { name: "הגישה שלי", href: "#trust" },
  { name: "צור קשר", href: "#contact" },
];

export function Layout({ children }: { children: React.ReactNode }) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const main = document.getElementById("main");
    const footer = document.querySelector("footer");

    if (mobileMenuOpen) {
      main?.setAttribute("inert", "");
      footer?.setAttribute("inert", "");
    } else {
      main?.removeAttribute("inert");
      footer?.removeAttribute("inert");
    }
  }, [mobileMenuOpen]);

  useEffect(() => {
    if (!mobileMenuOpen) return;

    const menu = menuRef.current;
    if (!menu) return;

    const focusableSelectors =
      'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
    const focusableElements =
      menu.querySelectorAll<HTMLElement>(focusableSelectors);

    const firstEl = focusableElements[0];
    const lastEl = focusableElements[focusableElements.length - 1];

    firstEl?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setMobileMenuOpen(false);
        menuButtonRef.current?.focus();
      }

      if (e.key === "Tab") {
        if (focusableElements.length === 0) return;

        if (e.shiftKey) {
          if (document.activeElement === firstEl) {
            e.preventDefault();
            lastEl?.focus();
          }
        } else {
          if (document.activeElement === lastEl) {
            e.preventDefault();
            firstEl?.focus();
          }
        }
      }
    };

    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("mobile-menu-state", {
        detail: { open: mobileMenuOpen },
      })
    );
    if (mobileMenuOpen) {
      document.body.classList.add("mobile-menu-open");
    } else {
      document.body.classList.remove("mobile-menu-open");
    }
  }, [mobileMenuOpen]);

  return (
    <div id="top" className="relative min-h-screen flex flex-col text-gray-100">
      <a
        href="#main"
        aria-label="דלג לתוכן הראשי"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:right-4 bg-blue-600 text-white px-4 py-2 rounded z-[9999]"
      >
        דלג לתוכן הראשי
      </a>
      <header
        role="banner"
        className={clsx(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300 ease-in-out border-b border-white/5 min-h-[96px] md:min-h-0",
          isScrolled
            ? "bg-slate-900/80 backdrop-blur-lg shadow-xl py-4 border-b border-white/5"
            : "bg-slate-950/40 backdrop-blur-md py-4"
        )}
      >
        <div className="container mx-auto px-6 relative flex items-center justify-between">
          {/* Desktop: בס״ד (כמו שהיה אצלך) */}
          <div className="hidden md:block text-slate-200/80 font-semibold tracking-wide">
            בס״ד
          </div>

          {/* Desktop: Logo - כמו המקורי */}
          <a href="#top" aria-label="דף הבית" className="hidden md:block">
            <img
              src={logo}
              alt="עו״ד ארז עזרא"
              width="192"
              height="64"
              className="w-[clamp(160px,35vw,220px)] h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-all duration-300"
            />
          </a>

          {/* Desktop Nav - כמו המקורי */}
          <nav
            className="hidden md:flex items-center gap-8"
            aria-label="ניווט ראשי"
          >
            {navItems.map((item) => {
              const isActive =
                typeof window !== "undefined" &&
                window.location.hash === item.href;

              return (
                <a
                  key={item.name}
                  href={item.href}
                  aria-current={isActive ? "page" : undefined}
                  className="text-gray-300 hover:text-blue-400 transition-colors font-medium relative group focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                >
                  {item.name}
                  <span className="absolute bottom-[-4px] right-0 h-0.5 bg-blue-600 transition-all duration-300 origin-right scale-x-0 group-hover:scale-x-100 w-full" />
                </a>
              );
            })}
            <a
              href="tel:035563036"
              className="px-5 py-2.5 bg-blue-600/90 backdrop-blur-md text-white rounded-full font-medium 
      hover:bg-blue-500 transition-all duration-300 flex items-center gap-2 shadow-lg hover:shadow-blue-500/40"
            >
              <Phone size={16} />
              <span>שיחת ייעוץ</span>
            </a>
          </nav>

          {/* Mobile: hamburger בימין */}
          <button
            ref={menuButtonRef}
            className="md:hidden z-50 p-2 text-gray-200"
            aria-label={
              mobileMenuOpen ? "סגירת תפריט ניווט" : "פתיחת תפריט ניווט"
            }
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-menu"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Mobile: בס״ד בשמאל */}
          <div className="md:hidden text-slate-200/80 font-semibold tracking-wide text-sm">
            בס״ד
          </div>

          {/* Mobile: Logo באמצע (בלי לשנות גודל/עיצוב של הדסקטופ) */}
          <a
            href="#top"
            aria-label="דף הבית"
            className="md:hidden absolute left-1/2 -translate-x-1/2"
          >
            <img
              src={logo}
              alt="עו״ד ארז עזרא"
              className="w-[clamp(160px,35vw,220px)] h-auto drop-shadow-[0_8px_24px_rgba(0,0,0,0.35)] transition-all duration-300"
            />
          </a>
        </div>

        <h2 id="mobile-menu-title" className="sr-only">
          תפריט ניווט
        </h2>
        {/* Mobile Nav Overlay */}
        <motion.nav
          ref={menuRef}
          id="mobile-menu"
          role="dialog"
          aria-modal="true"
          aria-labelledby="mobile-menu-title"
          aria-hidden={!mobileMenuOpen}
          tabIndex={-1}
          initial={false}
          animate={{
            x: mobileMenuOpen ? 0 : "100%",
            opacity: mobileMenuOpen ? 1 : 0,
          }}
          transition={{ type: "spring", damping: 25, stiffness: 200 }}
          style={{ top: 0, height: "100dvh" }}
          className="fixed inset-0 bg-slate-950 z-40 flex flex-col items-center justify-center gap-8 md:hidden"
        >
          {navItems.map((item) => (
            <a
              key={item.name}
              href={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className="text-2xl font-medium text-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
              {item.name}
            </a>
          ))}
          <a
            href="tel:035563036"
            className="px-8 py-3 bg-blue-600 text-white rounded-full text-lg font-medium"
          >
            03-5563036
          </a>
        </motion.nav>
      </header>

      <main id="main" className="flex-grow pt-20">
        {children}
      </main>

      <footer className="bg-gradient-to-b from-slate-950 to-black py-20 border-t border-slate-800">
        <div className="container mx-auto px-6 space-y-16">
          {/* Top Section */}
          <div className="grid md:grid-cols-2 gap-16 items-center text-center md:text-right">
            {/* Logo + Info */}
            <div className="mx-auto md:mx-0 w-full max-w-xl">
              <a href="#top" aria-label="חזרה לתחילת הדף">
                <img
                  src={logo}
                  alt="עו״ד ארז עזרא"
                  width="192"
                  height="64"
                  className="h-[110px] md:h-[110px] lg:h-[120px] w-auto mx-auto md:mx-0 mb-6 opacity-95 drop-shadow-[0_6px_20px_rgba(0,0,0,0.5)]"
                />
              </a>

              <p className="text-gray-300 text-base md:text-lg tracking-wide mb-6 font-light">
                דיני נזיקין וביטוח | משפט מסחרי
              </p>

              {/* Legal Links */}
              <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 text-[15px] md:text-base">
                <a
                  href="/accessibility"
                  className="text-gray-300 hover:text-blue-400 transition-colors underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                >
                  הצהרת נגישות
                </a>

                <span className="text-slate-700">|</span>

                <a
                  href="/privacy"
                  className="text-gray-300 hover:text-blue-400 transition-colors underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                >
                  מדיניות פרטיות
                </a>

                <span className="text-slate-700">|</span>

                <a
                  href="/terms"
                  className="text-gray-300 hover:text-blue-400 transition-colors underline underline-offset-4 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 rounded"
                >
                  תנאי שימוש
                </a>
              </div>
            </div>

            {/* Map Section */}
            <div className="w-full md:justify-self-start">
              <h3 className="text-lg font-semibold text-white mb-6">
                היכן אנו נמצאים
              </h3>

              <div className="relative w-full max-w-xl h-56 md:h-56 lg:h-52 mx-auto md:mx-0 rounded-2xl overflow-hidden border border-white/10 shadow-xl">
                {/* Map */}
                <iframe
                  title="מפת הגעה למשרד"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2435.3782968421374!2d34.86618280026331!3d32.0940930611707!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xcdcd38554a81115%3A0x68636f125e3ae886!2z15DXqNeWINei15bXqNeQINee16nXqNeTINei15XXqNeb15kg15PXmdef!5e0!3m2!1siw!2sil!4v1773930104289!5m2!1siw!2sil"
                  width="100%"
                  height="100%"
                  loading="lazy"
                  className="w-full h-full"
                />

                {/* Overlay Gradient */}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />

                {/* Floating Google Button */}
                <a
                  href="https://maps.app.goo.gl/U6x1H2jgnRSV9o3K8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="absolute top-3 left-3 bg-white/90 backdrop-blur-md text-black text-sm px-3 py-1.5 rounded-lg shadow-md hover:bg-white transition"
                >
                  פתיחה ב-Google Maps ↗
                </a>
              </div>

              {/* Buttons */}
              <div className="flex flex-wrap gap-3 mt-4">
                <a
                  href="https://maps.app.goo.gl/U6x1H2jgnRSV9o3K8"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-5 py-2 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-xl shadow-lg hover:shadow-blue-500/30 transition-all duration-300"
                >
                  Google Maps
                </a>

                <a
                  href="https://waze.com/ul/hsv8y96ed5"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3
             bg-slate-800 hover:bg-slate-700
             text-white font-medium rounded-xl
             transition-colors duration-200
             shadow-md hover:shadow-lg"
                >
                  <img
                    src={wazeIcon}
                    alt="Waze"
                    className="w-5 h-5 object-contain"
                  />
                  Waze
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Copyright */}
          <div className="border-t border-slate-800 pt-6 text-center">
            <p className="text-sm text-slate-300">
              © {new Date().getFullYear()} עו״ד ארז עזרא. כל הזכויות שמורות.
            </p>

            <p className="text-xs text-slate-400 mt-1 tracking-wide">
              Built by{" "}
              <a
                href="https://oevex.com/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="מעבר לאתר חברת Oevex - ייפתח בלשונית חדשה"
                className="text-slate-300 hover:text-white transition-colors duration-300"
              >
                Oevex
              </a>
            </p>

            <p className="text-xs text-slate-500 mt-4 max-w-xl mx-auto leading-relaxed">
              המידע המופיע באתר אינו מהווה ייעוץ משפטי ואינו מחליף ייעוץ משפטי
              פרטני.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
